import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { AppState } from '@app/app-state/models/app-state';
import { COUNTRIES } from '@app/constants/countries';
import {
  ConnectionToOtherLiquorLicencesComponent,
  ConnectionToOtherLiquorLicencesFormData
} from '@components/account-profile/tabs/connection-to-other-liquor-licences/connection-to-other-liquor-licences.component';
import {
  ConnectionToProducersComponent,
  ConnectionToProducersFormData
} from '@components/account-profile/tabs/connection-to-producers/connection-to-producers.component';
import {
  faAddressCard,
  faChevronRight,
  faEnvelope,
  faExclamationTriangle,
  faPhone,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { Account, BUSINESS_TYPE_LIST } from '@models/account.model';
import { Application } from '@models/application.model';
import { User } from '@models/user.model';
import { Store } from '@ngrx/store';
import { AccountDataService } from '@services/account-data.service';
import { ApplicationDataService } from '@services/application-data.service';
import { ContactDataService } from '@services/contact-data.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { TiedHouseConnectionsDataService } from '@services/tied-house-connections-data.service';
import { UserDataService } from '@services/user-data.service';
import { GenericMessageDialogComponent } from '@shared/components/dialog/generic-message-dialog/generic-message-dialog.component';
import { FormBase } from '@shared/form-base';
import { isFormValid } from '@shared/form-utils';
import { endOfToday } from 'date-fns';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, takeWhile } from 'rxjs/operators';
import { ApplicationTypeNames } from '../../models/application-type.model';

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const MY_FORMATS = {
  parse: {
    dateInput: 'LL'
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'YYYY-MM-DD',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

const ValidationFieldNameMap = {
  'businessProfile.id': 'Account ID',
  'businessProfile._mailingSameAsPhysicalAddress': 'Mailing Address Same as Physical Address',
  'businessProfile.bcIncorporationNumber': 'B.C. Incorporation Number',
  'businessProfile.dateOfIncorporationInBC': 'Date of Incorporation In B.C.',
  'businessProfile.businessNumber': 'Business Number',
  'businessProfile.businessType': 'Business Type',
  'businessProfile.contactPhone': 'Corporation Address Business Phone',
  'businessProfile.accountUrls': 'Account URL(s)',

  'businessProfile.physicalAddressStreet': 'Physical Address Street',
  'businessProfile.physicalAddressStreet2': 'Physical Address Street2',
  'businessProfile.physicalAddressCity': 'Physical Address City',
  'businessProfile.physicalAddressPostalCode': 'Physical Address Postal Code',
  'businessProfile.physicalAddressProvince': 'Physical Address Province',
  'businessProfile.physicalAddressCountry': 'Physical Address Country',
  'businessProfile.mailingAddressStreet': 'Mailing Address Street',
  'businessProfile.mailingAddressStreet2': 'Mailing Address Street2',
  'businessProfile.mailingAddressCity': 'Mailing Address City',
  'businessProfile.mailingAddressPostalCode': 'Mailing Address Postal Code',
  'businessProfile.mailingAddressProvince': 'Mailing Address Province',
  'businessProfile.mailingAddressCountry': 'Mailing Address Country',

  'contact.id': 'Corporation Contact ID',
  'contact.firstname': 'Corporation Contact First Name',
  'contact.lastname': 'Corporation Contact LastName',
  'contact.jobTitle': 'Corporation Contact Job Title',
  'contact.telephone1': 'Corporation Contact Telephone',
  'contact.emailaddress1': 'Corporation Contact Email',

  corpConnectionFederalProducerDetails: 'Corporation Federal Producer Details',
  federalProducerConnectionToCorpDetails: 'Immediate Family Member Federal Producer Details',
  share20PlusConnectionProducerDetails: 'Shareholder Federal Producer Details',
  partnersConnectionFederalProducerDetails: 'Partnership Federal Producer Details',
  societyConnectionFederalProducerDetails: 'Federal Producer Details',
  liquorFinancialInterestDetails: 'Federal Producer Details',
  iNConnectionToFederalProducerDetails: 'Federal Producer Details',

  hasLiquorTiedHouseOwnershipOrControl: 'Ownership or Control',
  hasLiquorTiedHouseThirdPartyAssociations: 'Third-Party Associations',
  hasLiquorTiedHouseFamilyMemberInvolvement: 'Immediate Family Member Involvement'
};

@Component({
  selector: 'app-account-profile',
  templateUrl: './account-profile.component.html',
  styleUrls: ['./account-profile.component.scss']
})
export class AccountProfileComponent extends FormBase implements OnInit {
  faExclamationTriangle = faExclamationTriangle;
  faTrash = faTrash;
  faChevronRight = faChevronRight;
  faAddressCard = faAddressCard;
  faEnvelope = faEnvelope;
  faPhone = faPhone;
  faPlus = faPlus;
  @Input()
  useInStepperMode = false;
  @Output()
  saveComplete = new EventEmitter<boolean>();
  currentUser: User;
  dataLoaded = false;
  busy: Subscription;
  form: FormGroup;
  countryList = COUNTRIES;
  maxDate = endOfToday();

  accountId: string;
  saveFormData: any;
  _showAdditionalAddress: boolean;
  _showAdditionalContact: boolean;
  legalEntityId: string;

  @ViewChild(ConnectionToProducersComponent)
  connectionToProducersComponent: ConnectionToProducersComponent;
  connectionToProducersFormData: ConnectionToProducersFormData;

  @ViewChild(ConnectionToOtherLiquorLicencesComponent)
  connectionToOtherLiquorLicencesComponent: ConnectionToOtherLiquorLicencesComponent;
  connectionToOtherLiquorLicencesFormData: ConnectionToOtherLiquorLicencesFormData;

  /**
   * The application ID under which this account profile is being edited.
   * This will only be set if this component is being used in the context of an application.
   */
  applicationId: string;
  applicationMode: string;
  account: Account;
  validationMessages: string[];
  renewalType: string;

  // Whether the ORV (Online Retailer Verification) feature is enabled (visible) or not.
  ORVFeatureEnabled: boolean = false;
  @ViewChild('badgeTemplateDialog') badgeTemplateDialog: TemplateRef<any>;
  generatedOrvCode: string = `<a href="#" onclick="window.open('https://orgbook-app-b7aa30-dev.apps.silver.devops.gov.bc.ca/verify/BC123456', '_blank', 'width=800,height=600'); return false;">Verify Retailer</a>`;

  hasLoadedData = false;

  get contacts(): FormArray {
    return this.form.get('otherContacts') as FormArray;
  }

  get accountUrls(): FormArray {
    return this.form.get('businessProfile.accountUrls') as FormArray;
  }

  /**
   * Regex validator that asserts a valid url string.
   *
   * @example
   * 'www.gov.bc.ca' // valid
   * 'bad string' // invalid
   */
  urlValidator = Validators.pattern(/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/);

  /**
   * Removes an account URL field from the form.
   */
  removeAccountUrl(index: number): void {
    const accountUrls = this.form.get('businessProfile.accountUrls') as FormArray;
    accountUrls.removeAt(index);
  }

  /**
   * Adds a new account URL field to the form.
   */
  addAccountUrl(): void {
    const accountUrls = this.form.get('businessProfile.accountUrls') as FormArray;
    accountUrls.push(this.fb.control('', [this.urlValidator]));
  }

  /**
   * Splits a comma separated string into an array of strings.
   * Trims each string, and removes empty strings.
   *
   * @param {(string | null)} csvString
   * @return {*}  {string[]} An array of strings. If the input string is null or empty, returns an array with an empty
   * string.
   * @memberof AccountProfileComponent
   */
  splitAccountURLString(csvString: string | null): string[] {
    const accountUrls =
      csvString
        ?.split(',')
        .map((item) => item.trim())
        .filter(Boolean) ?? [];

    if (accountUrls.length === 0) {
      // No initial account URLs, return an array with an empty string
      // to ensure the form control is initialized with at least one empty field
      return [''];
    }

    return accountUrls;
  }

  /**
   * Combines an array of strings into a comma separated string.
   * Trims each string, and removes empty strings.
   *
   * @param {(string[] | null)} strings
   * @return {*}  {string} A comma separated string. If the input array is null or empty, returns an empty string.
   * @memberof AccountProfileComponent
   */
  combineAccountURLStrings(strings: string[] | null): string {
    if (!strings?.length) {
      return '';
    }

    return strings
      .map((item) => item.trim())
      .filter(Boolean)
      .join(',');
  }

  businessTypes = BUSINESS_TYPE_LIST;

  constructor(
    private store: Store<AppState>,
    private accountDataService: AccountDataService,
    private contactDataService: ContactDataService,
    private userDataService: UserDataService,
    private applicationDataService: ApplicationDataService,
    private tiedHouseService: TiedHouseConnectionsDataService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    public featureFlagService: FeatureFlagService
  ) {
    super();
    combineLatest([this.route.paramMap, this.featureFlagService.featureOn('ORVEnabled')])
      .pipe(
        takeWhile(() => this.componentActive),
        switchMap(([params, orvEnabled]) => {
          this.renewalType = params.get('renewalType');
          this.applicationMode = params.get('mode');
          this.applicationId = params.get('applicationId');

          this.ORVFeatureEnabled = orvEnabled;

          if (this.applicationId) {
            return this.applicationDataService.getApplicationById(this.applicationId);
          }

          return of(null);
        })
      )
      .subscribe({
        next: (application) => {
          if (application) {
            this.application = application;
            this.connectionToOtherLiquorLicencesFormData = application.applicationExtension;
          }
          this.hasLoadedData = true;
        },
        error: (error) => {
          console.error('Error Loading Account Data', error);
          this.dialog.open(GenericMessageDialogComponent, {
            data: {
              title: 'Error Loading Account Data',
              message:
                'Failed to load Account data. Please try again. If the problem persists, please contact support.',
              closeButtonText: 'Close'
            }
          });
        }
      });
  }

  ngOnInit() {
    this.form = this.fb.group({
      businessProfile: this.fb.group({
        id: [''],
        _mailingSameAsPhysicalAddress: [],
        bcIncorporationNumber: [''],
        dateOfIncorporationInBC: [''],
        businessNumber: ['', [Validators.required, Validators.pattern('^[1-9][0-9]{8}$')]],
        businessType: ['', Validators.required],
        contactPhone: ['', [Validators.required]],
        contactEmail: ['', [Validators.required, Validators.email]],

        physicalAddressStreet: ['', Validators.required],
        physicalAddressStreet2: [''],
        physicalAddressCity: ['', Validators.required],
        physicalAddressPostalCode: ['', [Validators.required, this.customZipCodeValidator('physicalAddressCountry')]],
        physicalAddressProvince: ['British Columbia', Validators.required],
        physicalAddressCountry: ['Canada', Validators.required],
        mailingAddressStreet: ['', Validators.required],
        mailingAddressStreet2: [''],
        mailingAddressCity: ['', Validators.required],
        mailingAddressPostalCode: ['', [Validators.required, this.customZipCodeValidator('mailingAddressCountry')]],
        mailingAddressProvince: ['British Columbia', Validators.required],
        mailingAddressCountry: ['Canada', Validators.required],
        websiteUrl: [''],
        accountUrls: this.fb.array([])
      }),
      contact: this.fb.group({
        id: [],
        firstname: [{ value: '', disabled: true }, Validators.required],
        lastname: [{ value: '', disabled: true }, Validators.required],
        jobTitle: [''],
        telephone1: ['', [Validators.required]],
        emailaddress1: ['', [Validators.required, Validators.email]]
      })
    });
    // Watch for changes to the current user and account
    this.subscribeForData();

    this.form
      .get('businessProfile._mailingSameAsPhysicalAddress')
      .valueChanges.pipe(filter((value) => value === true))
      .subscribe(() => {
        this.copyPhysicalToMailingAddress();
      });

    this.form
      .get('businessProfile.physicalAddressStreet')
      .valueChanges.pipe(filter(() => this.form.get('businessProfile._mailingSameAsPhysicalAddress').value))
      .subscribe(() => {
        this.copyPhysicalToMailingAddress();
      });
    this.form
      .get('businessProfile.physicalAddressStreet2')
      .valueChanges.pipe(filter(() => this.form.get('businessProfile._mailingSameAsPhysicalAddress').value))
      .subscribe(() => {
        this.copyPhysicalToMailingAddress();
      });
    this.form
      .get('businessProfile.physicalAddressCity')
      .valueChanges.pipe(filter(() => this.form.get('businessProfile._mailingSameAsPhysicalAddress').value))
      .subscribe(() => {
        this.copyPhysicalToMailingAddress();
      });
    this.form
      .get('businessProfile.physicalAddressPostalCode')
      .valueChanges.pipe(filter(() => this.form.get('businessProfile._mailingSameAsPhysicalAddress').value))
      .subscribe(() => {
        this.copyPhysicalToMailingAddress();
      });

    this.form
      .get('businessProfile.physicalAddressProvince')
      .valueChanges.pipe(filter(() => this.form.get('businessProfile._mailingSameAsPhysicalAddress').value))
      .subscribe(() => {
        this.copyPhysicalToMailingAddress();
      });

    this.form
      .get('businessProfile.physicalAddressCountry')
      .valueChanges.pipe(filter(() => this.form.get('businessProfile._mailingSameAsPhysicalAddress').value))
      .subscribe(() => {
        this.copyPhysicalToMailingAddress();
      });
  }

  copyPhysicalToMailingAddress() {
    this.form
      .get('businessProfile.mailingAddressStreet')
      .patchValue(this.form.get('businessProfile.physicalAddressStreet').value);
    this.form
      .get('businessProfile.mailingAddressStreet2')
      .patchValue(this.form.get('businessProfile.physicalAddressStreet2').value);
    this.form
      .get('businessProfile.mailingAddressCity')
      .patchValue(this.form.get('businessProfile.physicalAddressCity').value);
    this.form
      .get('businessProfile.mailingAddressPostalCode')
      .patchValue(this.form.get('businessProfile.physicalAddressPostalCode').value);
    this.form
      .get('businessProfile.mailingAddressProvince')
      .patchValue(this.form.get('businessProfile.physicalAddressProvince').value);
    this.form
      .get('businessProfile.mailingAddressCountry')
      .patchValue(this.form.get('businessProfile.physicalAddressCountry').value);
  }

  getBusinessTypeName() {
    if (!(this.saveFormData && this.saveFormData.businessProfile) || this.account?.isOtherBusinessType()) {
      return '';
    }
    let name = '';
    switch (this.saveFormData.businessProfile.businessType) {
      case 'GeneralPartnership':
      case 'LimitedPartnership"':
      case 'LimitedLiabilityPartnership':
        name = 'Partnership';
        break;
      case 'SoleProprietorship':
        name = 'Sole Proprietorship';
        break;
      case 'IndigenousNation':
        name = 'Indigenous Nation';
        break;
      case 'LocalGovernment':
        name = 'Local Government';
        break;
      case 'Police':
        name = 'Police';
        break;
      case 'PublicCorporation':
      case 'PrivateCorporation':
      case 'UnlimitedLiabilityCorporation':
      case 'LimitedLiabilityCorporation':
        name = 'Corporation';
        break;
      default:
        name = this.saveFormData.businessProfile.businessType;
        break;
    }
    return name;
  }

  legalNameLabel() {
    const businessType = this.getBusinessTypeName();
    let label = `${businessType} ${this.account?.isOtherBusinessType() ? '' : '-'} Legal Name`;
    if (businessType === 'IndigenousNation') {
      label = 'Full name of Indigenous Nation';
    }
    return label;
  }

  subscribeForData() {
    this.store
      .select((state) => state.currentAccountState.currentAccount)
      .pipe(takeWhile(() => this.componentActive))
      .pipe(filter((s) => !!s))
      .subscribe((account) => this.loadAccount(account));

    this.store
      .select((state) => state.currentUserState.currentUser)
      .pipe(takeWhile(() => this.componentActive))
      .pipe(filter((user) => !!user))
      .subscribe((user) => this.loadUser(user));
  }

  private loadAccount(account: Account) {
    this.account = account;

    // Make a copy of the account object stored in Ngrx (which is read-only)
    // Remove `accountUrls` as the API returns a string, but the form is expecting an array.
    // See https://stackoverflow.com/questions/57591012/ngrx-cannot-assign-to-read-only-property-property-of-object-object
    const { accountUrls, ...businessProfile }: Partial<Account> = { ...account };

    businessProfile.physicalAddressProvince = businessProfile.physicalAddressProvince || 'British Columbia';
    businessProfile.physicalAddressCountry = 'Canada';
    businessProfile.mailingAddressProvince = businessProfile.mailingAddressProvince || 'British Columbia';
    businessProfile.mailingAddressCountry = 'Canada';

    this.form.patchValue({ businessProfile: businessProfile });

    this.saveFormData = this.form.value;

    // normalize postal codes
    this.form
      .get('businessProfile.mailingAddressPostalCode')
      .setValue((this.form.get('businessProfile.mailingAddressPostalCode').value || '').replace(/\s+/g, ''));
    this.form
      .get('businessProfile.physicalAddressPostalCode')
      .setValue((this.form.get('businessProfile.physicalAddressPostalCode').value || '').replace(/\s+/g, ''));

    // LCSD-7412
    // Simplified vaidators for bcIncorporationNumber
    // Incorporation Number	|incorporationNumber:	Registry number for Corporations |	Up to 10 alphanumeric characters.
    if (this.account.isPrivateCorporation() || this.account.businessType === 'Society') {
      this.form.get('businessProfile.bcIncorporationNumber').setValidators([Validators.pattern('^[A-Za-z0-9]{1,15}$')]);
    } else {
      this.form.get('businessProfile.bcIncorporationNumber').clearValidators();
    }

    // Transform the accountUrls comma-separated string into an array
    const accountUrlsArray = this.splitAccountURLString(accountUrls);
    const accountUrlsArrayControl = this.form.get('businessProfile.accountUrls') as FormArray;
    // Clear the existing account form controls, if any, so duplicate controls are not created if this function is
    // called multiple times
    accountUrlsArrayControl.clear();
    for (const accountUrl of accountUrlsArray) {
      // Add a form control for each account URL
      accountUrlsArrayControl.push(this.fb.control(accountUrl, [this.urlValidator]));
    }
  }

  private loadUser(user: User) {
    this.currentUser = user;
    if (this.currentUser && this.currentUser.contactid) {
      this.contactDataService.getContact(this.currentUser.contactid).subscribe((contact) => {
        this.form.patchValue({ contact: contact });
        this.saveFormData = this.form.value;
      });
    }
  }

  /**
   * Checks if the component can be deactivated.
   *
   * @return {*}  {Observable<boolean>}
   */
  canDeactivate(): Observable<boolean> {
    if (!this.hasFormChanged() && !this.connectionToOtherLiquorLicencesComponent.formHasChanged()) {
      return of(true);
    } else {
      return this.save();
    }
  }

  /**
   * Checks if the form data has changed from the saved form data.
   *
   * @return {*}  {boolean}
   */
  hasFormChanged(): boolean {
    if (JSON.stringify(this.saveFormData) !== JSON.stringify(this.form.value)) {
      return true;
    }

    return false;
  }

  openBadgeTemplateDialog() {
    this.dialog.open(this.badgeTemplateDialog, {
      disableClose: true,
      autoFocus: true,
      width: 'auto',
      height: 'auto',
      maxWidth: '500px',
      maxHeight: '80vh',
      panelClass: 'custom-dialog-container'
    });
  }

  // 2024-09-13: Temporary disabling this until further development work can be completed to support this feature.
  onCopy(): void {
    this.clipboard.copy(this.generatedOrvCode);
    this.snackBar.open('HTML copied to clipboard', null, {
      duration: 2000
    });
    this.dialog.closeAll();
  }

  save(): Observable<boolean> {
    this.form.get('businessProfile').patchValue({ physicalAddressCountry: 'Canada' });

    const value = {
      ...this.form.get('businessProfile').value,
      // Transform the accountUrls array into a comma-separated string as expected by the API
      accountUrls: this.combineAccountURLStrings(this.form.get('businessProfile.accountUrls').value)
    } as Account;

    const saves = [
      this.accountDataService.updateAccount(value),
      this.contactDataService.updateContact((this.form.get('contact') as FormGroup).getRawValue())
    ];

    // Save the cannabis tied house connection form data
    if (this.connectionToProducersFormData) {
      if (this.connectionToProducersFormData.id) {
        // If we have a primary id, update the existing cannabis tied house connection
        saves.push(
          this.tiedHouseService.updateCannabisTiedHouseConnection(
            this.connectionToProducersFormData,
            this.connectionToProducersFormData.id
          )
        );
      } else {
        // Create a new cannabis tied house connection
        saves.push(
          this.tiedHouseService.upsertCannabisTiedHouseConnection(this.connectionToProducersFormData, this.account.id)
        );
      }
    }

    // Save the connection to other liquor licences form data
    if (this.connectionToOtherLiquorLicencesFormData) {
      // Only persist the connection to other liquor licences form data if this component is being used in the context
      // of an application.
      if (this.applicationId) {
        const updatedApplicationExtensionData = {
          ...this.application.applicationExtension,
          ...this.connectionToOtherLiquorLicencesFormData
        };

        const updatedApplicationData: Application = {
          ...this.application,
          applicationExtension: updatedApplicationExtensionData
        };

        saves.push(this.applicationDataService.updateApplication(updatedApplicationData));
      }
    }

    return forkJoin(saves).pipe(
      catchError(() => of(false)),
      map(() => {
        this.accountDataService.loadCurrentAccountToStore(this.account.id).subscribe(() => {});
        // reload the user to fetch updated contact information
        this.userDataService.loadUserToStore().then(() => {});
        return true;
      })
    );
  }

  /**
   * Checks if the form is valid. Ignores sections that are not displayed.
   *
   * @readonly
   * @type {boolean} `true` if the form is valid or disabled, `false` otherwise.
   */
  get _isFormValid(): boolean {
    return (
      isFormValid(this.form) &&
      (this.showConnectionsToProducersSection ? isFormValid(this.connectionToProducersComponent?.form) : true) &&
      (this.showConnectionToOtherLiquorLicencesSection
        ? isFormValid(this.connectionToOtherLiquorLicencesComponent?.form)
        : true)
    );
  }

  /**
   * Whether or not to display the Connections To Producers Section.
   *
   * @readonly
   * @type {boolean}
   */
  get showConnectionsToProducersSection(): boolean {
    return (
      !this.isEthylAlcoholPermit &&
      this.saveFormData?.businessProfile?.id &&
      ['Police'].indexOf(this.getBusinessTypeName()) == -1
    );
  }

  /**
   * Whether or not to display the Connection To Other Liquor Licences Section.
   *
   * @readonly
   * @type {boolean}
   */
  get showConnectionToOtherLiquorLicencesSection(): boolean {
    return true;
  }

  get isEthylAlcoholPermit(): boolean {
    return this.application?.applicationType?.name === 'Ethyl Alcohol Permit';
  }

  gotoReview() {
    this.validationMessages = [];

    var route = '/dashboard';

    if (this.getBusinessTypeName() == 'Police') {
      this.form.get('businessProfile.businessNumber').setValidators([]);
      this.form.get('businessProfile.businessNumber').updateValueAndValidity();
      route = '/sep/dashboard';
    }

    if (!this._isFormValid) {
      this.markAsTouched();

      this.validationMessages = this.getFormValidationErrorMessages();

      return;
    }

    this.busy = this.save().subscribe(() => {
      if (this.useInStepperMode) {
        this.saveComplete.emit(true);
      } else if (this.applicationId) {
        if (this.application?.applicationType?.name === ApplicationTypeNames.TiedHouseExemptionApplication) {
          const route: any[] = [`/tied-house-exemption/${this.applicationId}`];
          this.router.navigate(route);
        }

        if (this.renewalType) {
          const route: any[] = [`/renew-licence/${this.renewalType}/${this.applicationId}`];
          if (this.applicationMode) {
            route.push({ mode: this.applicationMode });
          }
          this.router.navigate(route);
        } else if (this.applicationMode === 'catering') {
          // divert catering
          const route: any[] = [`/application/catering/${this.applicationId}`];
          if (this.applicationMode) {
            route.push({ mode: this.applicationMode });
          }
          this.router.navigate(route);
        } else {
          const route: any[] = [`/application/${this.applicationId}`];
          if (this.applicationMode) {
            route.push({ mode: this.applicationMode });
          }
          this.router.navigate(route);
        }
      } else {
        this.router.navigate([route]);
      }
    });
  }

  /**
   * Mark the forms as touched to trigger the display of validation error messages.
   */
  markAsTouched() {
    this.form.markAsTouched();
    this.connectionToProducersComponent?.form?.markAllAsTouched();
    this.connectionToOtherLiquorLicencesComponent?.form?.markAllAsTouched();

    const businessProfileControls = (this.form.get('businessProfile') as FormGroup).controls;
    for (const businessProfileControl in businessProfileControls) {
      if (typeof businessProfileControls[businessProfileControl].markAsTouched === 'function') {
        businessProfileControls[businessProfileControl].markAsTouched();
      }
    }

    const contactControls = (this.form.get('contact') as FormGroup).controls;
    for (const contactControl in contactControls) {
      if (typeof contactControls[contactControl].markAsTouched === 'function') {
        contactControls[contactControl].markAsTouched();
      }
    }
  }

  /**
   * Get form validation error messages.
   *
   * @return {*}  {string[]}
   */
  getFormValidationErrorMessages(): string[] {
    return [
      ...this.listControlsWithErrors(this.form, ValidationFieldNameMap),
      ...this.listControlsWithErrors(this.connectionToProducersComponent?.form, ValidationFieldNameMap),
      ...this.listControlsWithErrors(this.connectionToOtherLiquorLicencesComponent?.form, ValidationFieldNameMap)
    ];
  }

  /**
   * Whether the ORV (Online Retail Verification) button is enabled (clickable) or not.
   *
   * @return {*}  {boolean}
   * @memberof AccountProfileComponent
   */
  isORVButtonEnabled(): boolean {
    if (!this.ORVFeatureEnabled) {
      // ORV is disabled at the feature flag level.
      return false;
    }

    if (this.accountUrls?.controls?.length === 0) {
      // No account URLs specified, ORV is enabled.
      return true;
    }

    if (this.accountUrls?.controls?.length === 1 && this.accountUrls?.controls?.[0].value === '') {
      // On account URL is specified (default empty field), but it is empty, ORV is enabled.
      return true;
    }

    // One or more account URLs are specified, ORV is disabled.
    return false;
  }
}
